import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody } from "../../components/card/card";
import { chatAssistant, user } from "../../api";
import { toast } from "react-toastify";
import AddMachine from "../assets/add";
import { useLocation, useNavigate } from "react-router-dom";
import { AssignedTo } from "./AssignedTo";
import { SafeHtml } from "../../components/SafeHtml";
import { getFileUrl } from "../../utils/helper";
import { formatTimestamp } from "../../utils/formatHelpers";

export default function ChatAssistant({ onMessageUpdate, open }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({ page: 1, take: 30, totalData: 1 });
  const [waitingReply, setWaitingReply] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isSupported, setIsSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [recognitionInstance, setRecognitionInstance] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState({});
  const [downvoteReasons, setDownvoteReasons] = useState({});
  const [showReasonInput, setShowReasonInput] = useState({});
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedPredefinedReason, setSelectedPredefinedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US"; // Set the language for speech recognition
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const speechResult = event.results[0][0].transcript;
        sendMessage(speechResult);
        setListening(false);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setListening(false);
      };

      setRecognitionInstance(recognition);
      setIsSupported(true);
    } else {
      setIsSupported(false);
    }
  }, []);
  const handleListen = () => {
    if (recognitionInstance) {
      setListening(true);
      setTranscript(""); // Optional: clear previous result
      recognitionInstance.start();
    }
  };

  const navigate = useNavigate();

  const location = useLocation();

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];

    // Validate each file
    const validFiles = files.filter((file) => {
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          `${file.name}: Please upload an image (JPG, PNG, GIF) or PDF file`
        );
        return false;
      }
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: File size should be less than 5MB`);
        return false;
      }
      return true;
    });

    setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    // Update input to show number of files
    setInput(`Uploading ${validFiles.length} file(s)`);
  };

  const clearFileSelection = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setInput("");
  };

  const removeFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    if (selectedFiles.length <= 1) {
      setInput("");
    } else {
      setInput(`Uploading ${selectedFiles.length - 1} file(s)`);
    }
  };

  const sendMessage = async (msg = "", type) => {
    if (!msg && selectedFiles.length === 0) {
      if (!input.trim()) return;
    }

    const text = msg ? msg : input;

    const formData = new FormData();
    formData.append("message", text);
    if (type) {
      formData.append("type", type);
    }

    setInput("");
    setMessages((prev) => [
      ...prev,
      {
        content: { text: String(text), type: type },
        role: "user",
        createdAt: new Date(),
      },
    ]);
    setWaitingReply(true);
    setLoading(true);

    try {
      let response;
      if (selectedFiles.length > 0) {
        // Append all selected files
        selectedFiles.forEach((file, index) => {
          formData.append(`files`, file);
        });
        response = await chatAssistant.uploadFile(formData);
        //if upload file success, then add file key to message
        if (response.ok) {
          const files = await response.json();
          const newFormData = new FormData();
          newFormData.append("type", "files");
          newFormData.append(
            "message",
            `${files.length} files uploaded : ${files.join(", ")}`
          );
          newFormData.append("files", JSON.stringify(files));
          response = await chatAssistant.create(newFormData);
          if (response.ok) {
            const data = await response.json();
            setMessages((prev) => [...prev, data]);
          } else {
            toast.error(
              "Something went wrong with our system, please try again"
            );
          }
        }
      } else {
        response = await chatAssistant.create(formData);
        if (response.ok) {
          const data = await response.json();
          setMessages((prev) => [...prev, data]);
        } else {
          toast.error("Something went wrong with our system, please try again");
        }
      }
    } catch (e) {
      toast.error("Something went wrong with our system, please try again");
    } finally {
      setLoading(false);
      setWaitingReply(false);
      clearFileSelection();
    }
  };

  async function getData() {
    try {
      setLoading(true);
      const queryParam = {
        ...query,
      };
      const res = await chatAssistant.list(queryParam);
      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data && data.results) {
          setMessages(data.results.reverse());
        }
        if (data.totalData) {
          setQuery((prev) => ({
            ...prev,
            totalData: data.totalData,
          }));
        }
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e.message);
      console.error(e);
    }
  }

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [query.page, query.take]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") sendMessage();
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    if (open)
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
  }, [open]);

  const afterSubmitAssetCallback = ({ type, data }) => {
    if (type == "submit") {
      sendMessage(`New asset "${data.asset_name}" (id:${data.id}) created`);
    } else {
      sendMessage("canceling create new asset");
    }
  };

  const afterSubmitAssignedToCallback = (data) => {
    sendMessage(
      `Form submitted: Work order assigned to "${data.name}" (id:${data.value})`
    );
  };

  const isGeneratedBySystem = (text) => {
    if (text && typeof text === "string" && text.includes("id:")) {
      return true;
    }

    return false;
  };

  const handleMarkFeedback = async (messageId, feedback, reason = null) => {
    // Set loading state for this specific message
    setFeedbackLoading((prev) => ({ ...prev, [messageId]: true }));

    try {
      const formData = new FormData();
      formData.append("messageId", messageId);
      formData.append("feedback", feedback);
      if (reason) {
        formData.append("reason", reason);
      }

      const response = await chatAssistant.markMessageFeedback(
        messageId,
        feedback,
        reason
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Message marked as ${feedback} answer`);

        // Update the message in the local state to reflect the feedback
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  feedback: feedback,
                  feedbackReason: reason || null,
                  feedbackAt: new Date(),
                }
              : msg
          )
        );

        // Clear the reason input and hide it
        setDownvoteReasons((prev) => ({ ...prev, [messageId]: "" }));
        setShowReasonInput((prev) => ({ ...prev, [messageId]: false }));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to mark feedback");
      }
    } catch (error) {
      console.error("Error marking feedback:", error);
      toast.error("Failed to mark feedback");
    } finally {
      // Clear loading state
      setFeedbackLoading((prev) => ({ ...prev, [messageId]: false }));
    }
  };

  const predefinedReasons = [
    "Didn't fully follow instructions",
    "Not factually correct",
    "Not relevant",
    "Is not what was requested",
  ];

  const handleDownvoteClick = (messageId) => {
    // Show feedback modal for this message
    setSelectedMessageId(messageId);
    setShowFeedbackModal(true);
    setSelectedPredefinedReason("");
    setCustomReason("");
  };

  const handleSubmitFeedback = () => {
    const finalReason = selectedPredefinedReason || customReason.trim();

    if (!finalReason) {
      toast.error("Please provide a reason for the downvote");
      return;
    }

    if (selectedMessageId) {
      handleMarkFeedback(selectedMessageId, "bad", finalReason);
      handleCloseFeedbackModal();
    }
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setSelectedMessageId(null);
    setSelectedPredefinedReason("");
    setCustomReason("");
  };

  const handlePredefinedReasonSelect = (reason) => {
    setSelectedPredefinedReason(reason);
    setCustomReason(""); // Clear custom reason when predefined is selected
  };

  const handleCustomReasonChange = (value) => {
    setCustomReason(value);
    setSelectedPredefinedReason(""); // Clear predefined when custom is typed
  };
  useEffect(() => {
    if (messages.length > 0 && onMessageUpdate) {
      onMessageUpdate(messages[messages.length - 1]);
    }
  }, [messages]);

  useEffect(() => {
    // sendMessage(`i am visiting page ${location.pathname}`, "action");
  }, [location.pathname]);

  // Handle Escape key for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showFeedbackModal) {
        handleCloseFeedbackModal();
      }
    };

    if (showFeedbackModal) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [showFeedbackModal]);

  return (
    <div style={{ padding: "10px" }}>
      <style>
        {`
            .feedback-btn {
              transition: all 0.2s ease;
              border-radius: 4px;
            }
            .feedback-btn:hover {
              transform: scale(1.1);
            }
            .feedback-btn.active {
              box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
            }
            .reason-input-container {
              animation: slideDown 0.3s ease-out;
            }
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            .feedback-modal {
              animation: fadeIn 0.3s ease-out;
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            .predefined-reason-btn {
              transition: all 0.2s ease;
            }
            .predefined-reason-btn:hover {
              background-color: #f8f9fa;
            }
            .predefined-reason-btn.active {
              background-color: #e9ecef;
              border-color: #6c757d;
            }
          `}
      </style>
      <Card className="border-theme">
        <CardBody className={"p-0-mobile"}>
          <div className="mb-4 h-full-mobile" style={{ overflowY: "auto" }}>
            <div className="card-body">
              {messages
                .filter((msg) => msg.content.type !== "action")
                .map((msg, index) => (
                  <div
                    key={index}
                    className={`d-flex w-100 mb-4 ${
                      msg.role == "user" &&
                      !isGeneratedBySystem(msg?.content?.text)
                        ? "justify-content-end"
                        : ""
                    }`}
                  >
                    <div
                      className={`message-box ${
                        msg.role === "user" &&
                        !isGeneratedBySystem(msg?.content?.text)
                          ? "user-message"
                          : "assistant-message"
                      } ${index == messages.length - 1 ? "fade-in" : ""}`}
                    >
                      {msg.content.type != "files" && (
                        <>
                          <SafeHtml
                            text={msg?.content?.text || msg.content.reply}
                          />
                          {msg.feedback === "bad" && msg.feedbackReason && (
                            <div className="mt-2 p-2 border-start border-danger border-3 bg-dark">
                              <small className="text-muted d-block mb-1">
                                <i className="fas fa-exclamation-triangle text-danger me-1"></i>
                                Feedback provided:
                                {msg.feedbackAt && (
                                  <span className="ms-2 opacity-75">
                                    {formatTimestamp(msg.feedbackAt)}
                                  </span>
                                )}
                              </small>
                              <small className="text-muted">
                                {msg.feedbackReason}
                              </small>
                            </div>
                          )}
                        </>
                      )}
                      {msg.content.type == "files" && (
                        <>
                          <div>{msg.content.files.length} files uploaded</div>
                          {msg.content.files.map((file, index) => {
                            return (
                              <div className="mt-2" key={index}>
                                <a
                                  href={getFileUrl(file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  View Uploaded File ({index + 1})
                                </a>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {msg.content.metadata &&
                        Object.keys(msg.content.metadata).length > 0 &&
                        Object.keys(msg.content.metadata).map((key) => (
                          <div className="mt-2" key={key}>
                            {msg.content.metadata[key].form_type == "asset" && (
                              <>
                                {messages[index + 1] ? (
                                  <div style={{ color: "grey" }}>
                                    Action already taken
                                  </div>
                                ) : (
                                  <AddMachine
                                    preFilledData={
                                      msg.content.metadata[key]
                                        .asset_pre_filled_data
                                    }
                                    afterSubmitCallback={
                                      afterSubmitAssetCallback
                                    }
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ))}

                      {/* Timestamp and Feedback Buttons */}
                      <div className="mt-2 d-flex justify-content-between align-items-center">
                        {msg.createdAt && (
                          <small className="text-muted opacity-50">
                            {formatTimestamp(msg.createdAt)}
                          </small>
                        )}

                        {/* Feedback buttons - only show for assistant messages */}
                        {msg.role === "assistant" && msg.id && (
                          <div className="d-flex gap-1">
                            <button
                              className={`btn btn-sm feedback-btn ${
                                msg.feedback === "good"
                                  ? "btn-success active"
                                  : "btn-outline-success"
                              }`}
                              onClick={() => handleMarkFeedback(msg.id, "good")}
                              title="Mark as good answer"
                              disabled={feedbackLoading[msg.id]}
                              style={{
                                fontSize: "0.75rem",
                                padding: "0.25rem 0.5rem",
                              }}
                            >
                              {feedbackLoading[msg.id] ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-thumbs-up"></i>
                              )}
                            </button>
                            <button
                              className={`btn btn-sm feedback-btn ${
                                msg.feedback === "bad"
                                  ? "btn-danger active"
                                  : "btn-outline-danger"
                              }`}
                              onClick={() => handleDownvoteClick(msg.id)}
                              title="Mark as bad answer"
                              disabled={feedbackLoading[msg.id]}
                              style={{
                                fontSize: "0.75rem",
                                padding: "0.25rem 0.5rem",
                              }}
                            >
                              {feedbackLoading[msg.id] ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-thumbs-down"></i>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              {waitingReply && (
                <div>
                  <div className="spinner-grow text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>
          </div>
          <textarea
            type="text"
            className="form-control form-control-lg"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={loading}
          />
          <div className="input-group mb-1">
            <div className="d-flex justify-content-between w-100 mt-1">
              <div>
                <button
                  className="btn btn-outline-secondary px-3 me-2"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  title="Attach files"
                >
                  <i className="fas fa-paperclip"></i>
                </button>
              </div>
              <div>
                {isSupported ? (
                  <button
                    className="btn btn-theme text-black me-1"
                    type="button"
                    onClick={handleListen}
                    disabled={loading}
                  >
                    <i className="fa fa-microphone"></i>
                  </button>
                ) : null}

                <button
                  className="btn btn-theme text-black px-4"
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={loading}
                >
                  <i className="fas fa-paper-plane me-2"></i>
                  Send
                </button>
              </div>
            </div>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept="image/jpeg,image/png,image/gif,application/pdf"
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="selected-files-container bg-light p-3 rounded mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">Selected Files:</small>
                <button
                  className="btn btn-sm btn-link text-danger"
                  onClick={clearFileSelection}
                >
                  Clear all
                </button>
              </div>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="d-flex align-items-center justify-content-between p-2 border-bottom"
                >
                  <div className="d-flex align-items-center">
                    <i className="fas fa-file me-2 text-primary"></i>
                    <small className="text-muted">
                      {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                    </small>
                  </div>
                  <button
                    className="btn btn-sm text-danger"
                    onClick={() => removeFile(index)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div
          className="modal fade show d-block feedback-modal"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              handleCloseFeedbackModal();
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                  What went wrong?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseFeedbackModal}
                  disabled={feedbackLoading[selectedMessageId]}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  Help us improve by telling us what could be better about this
                  response.
                </p>

                {/* Predefined Reasons */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Common issues:</label>
                  <div className="d-grid gap-2">
                    {predefinedReasons.map((reason, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`btn btn-outline-secondary text-start predefined-reason-btn ${
                          selectedPredefinedReason === reason ? "active" : ""
                        }`}
                        onClick={() => handlePredefinedReasonSelect(reason)}
                        disabled={feedbackLoading[selectedMessageId]}
                      >
                        <i
                          className={`fas fa-check me-2 ${
                            selectedPredefinedReason === reason
                              ? "text-success"
                              : "text-muted"
                          }`}
                        ></i>
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Reason */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Or tell us more:</label>
                  <textarea
                    className="form-control"
                    placeholder="Describe what could be improved about this response..."
                    value={customReason}
                    onChange={(e) => handleCustomReasonChange(e.target.value)}
                    rows={3}
                    disabled={feedbackLoading[selectedMessageId]}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseFeedbackModal}
                  disabled={feedbackLoading[selectedMessageId]}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleSubmitFeedback}
                  disabled={
                    feedbackLoading[selectedMessageId] ||
                    (!selectedPredefinedReason && !customReason.trim())
                  }
                >
                  {feedbackLoading[selectedMessageId] ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
